// lib/actions.ts
import { supabase } from "@/lib/supabase";
// import { supabaseFetch } from './fetch'
import type {
  Business,
  Branch,
  BranchMenuItemServiceOverride,
  Menu,
  MenuCollection,
  MenuItem,
  MenuItemAvailabilityException,
  MenuItemAvailabilityRule,
  MenuItemOption,
  MenuItemOptionGroup,
  User,
  MenuCategories
} from "./types";

// ------------------------
// 🧑‍💼 USER UTILITIES
// ------------------------

async function requireAuth() {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error || !user) {
    throw new Error('Not authenticated');
  }
  return user;
}


// ✅ Fetch Business by Slug
export async function getBusinessBySlug(
  slug: string
): Promise<(Business & { branches: Branch[]; menus: Menu[] }) | null> {
  const { data, error } = await supabase
    .from("businesses")
    .select("*, branches(*), menus(*)")
    .eq("slug", slug)
    .maybeSingle();

  if (error) throw error;
  return data ?? null;
}
export async function getBusinessByUser(): Promise<Business | null> {
  const user = (await supabase.auth.getUser()).data.user
  if (!user) throw new Error('Not authenticated')

  const { data, error } = await supabase
    .from('businesses')
    .select('*, menus(*)')
    .eq('owner_id', user.id)
    .maybeSingle()

  if (error) throw error
  return data
}



export async function getMenuBySlug(slug: string): Promise<Menu | null> {
  const { data, error } = await supabase
    .from("menus")
    .select("*")
    .eq("slug", slug)
    .maybeSingle();

  if (error) throw error;
  return data;
}

// ✅ Fetch Business by ID
export async function getBusinessById(id: string): Promise<Business | null> {
  const { data, error } = await supabase
    .from("businesses")
    .select("*")
    .eq("id", id)
    .maybeSingle();

  if (error) throw error;
  return data;
}

// ✅ Fetch branch by ID
export async function getBranchById(id: string): Promise<Branch | null> {
  if (!id) return null;
  const { data, error } = await supabase
    .from("branches")
    .select("*")
    .eq("id", id)
    .maybeSingle();

  if (error) throw error;
  return data ?? null;
}


// Update Menu
export async function updateMenu(menuId: string, updates: Partial<Menu>) {
  await requireAuth();
  const { error } = await supabase
    .from('menus')
    .update(updates)
    .eq('id', menuId)
  if (error) throw error
}

// Delete Menu
export async function deleteMenu(menuId: string) {
  await requireAuth();
  const { error } = await supabase
    .from('menus')
    .delete()
    .eq('id', menuId)
  if (error) throw error
}

// Update Menu Item
export async function updateMenuItem(itemId: string, updates: Partial<MenuItem>) {
  await requireAuth();
  const { error } = await supabase
    .from('menu_items')
    .update(updates)
    .eq('id', itemId)
  if (error) throw error

  // D10: regenerate embedding in background (non-blocking, fire-and-forget).
  // Never await — embedding failure must not block the save flow.
  // Uses fetch to the API route (not a dynamic import of embeddings.ts)
  // because embeddings.ts chains to catalog.ts → branch-catalog.ts → server-only,
  // and this server action is imported by client components in the dashboard.
  fetch(`/api/ai/embeddings/generate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ itemId }),
  }).catch(() => {});
}

// Delete Menu Item
export async function deleteMenuItem(itemId: string) {
  await requireAuth();
  const { error } = await supabase
    .from('menu_items')
    .delete()
    .eq('id', itemId)
  if (error) throw error
}



// ✅ Get all menus (for SSG paths)
export async function getAllMenus(options?: { limit?: number; offset?: number }): Promise<Menu[]> {
  let query = supabase
    .from('menus')
    .select('id, slug, business_id, title');

  if (options?.limit) {
    query = query.limit(options.limit);
  }
  if (options?.offset) {
    query = query.range(options.offset, options.offset + (options.limit ?? 1000) - 1);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}



// ✅ Get promotions for a menu
export async function getPromotions(menuId: string): Promise<any[]> {
  if (!menuId) return [];
  const { data, error } = await supabase
    .from('promotions')
    .select('*')
    .or(`menu_id.eq.${menuId},menu_id.is.null`);
  if (error) throw error;
  return data ?? [];
}

// Find menus with similar slugs (e.g., using ILIKE or fuzzy matching)
export async function getMenusBySimilarSlug(slug: string) {
  // Example: Use Supabase/Postgres ILIKE for partial match
  const { data, error } = await supabase
    .from('menus')
    .select('id, slug, title, business:business_id (id, name, logo_url, description)')
    .ilike('slug', `%${slug}%`)
    .limit(12);
  if (error) throw error;
  return data ?? [];
}

// Get all menus with business info
export async function getAllMenusWithBusiness(options?: { limit?: number; offset?: number }) {
  let query = supabase
    .from('menus')
    .select('id, slug, title, business:business_id (id, name, logo_url, description)')
    .order('created_at', { ascending: false });

  if (options?.limit) {
    query = query.limit(options.limit);
  }
  if (options?.offset) {
    query = query.range(options.offset, options.offset + (options.limit ?? 1000) - 1);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}





// ✅ Get menu collections for a menu
export async function getMenuCollections(menuId: string): Promise<MenuCollection[]> {
  if (!menuId) return [];
  const { data, error } = await supabase
    .from('menu_collections')
    .select('id, menu_id, name, slug, description, icon, sort_order, is_default, is_active, created_at, updated_at')
    .eq('menu_id', menuId)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

// ✅ Get menu categories for a menu
export async function getMenuCategories(menuId: string): Promise<MenuCategories[]> {
  if (!menuId) return [];
  const { data, error } = await supabase
    .from('menu_categories')
    .select('id, menu_id, collection_id, name, description, category_type, display_order, is_active, created_at, updated_at')
    .eq('menu_id', menuId)
    .order('collection_id', { ascending: true, nullsFirst: false })
    .order('display_order', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

// ✅ Get menu items for a menu (already exists, but re-export for clarity)
// PUBLIC loader (the /m menu page, previews, TV display): runs with the anon key and
// its result is serialised into the page. It must never select internal columns:
// cost_price used to be here and shipped to every guest's browser. The column is
// also revoked from `anon` (20261120000000_menu_items_cost_price_private.sql).
export async function getMenuItems(menuId: string): Promise<MenuItem[]> {
  if (!menuId) return [];
  const { data, error } = await supabase
    .from('menu_items')
    .select('id, menu_id, title, description, price, kind, pricing_mode, type_profile, price_per_unit_weight, weight_unit, min_weight, max_weight, image, allergens, allergens_declared, dietary_tags, nutritional_info, category_id, display_order, is_available, is_featured, is_coming_soon, is_alcoholic, tags, variants, available_Days, available_Times, available_from, available_until, sell_state, sell_state_reason, stop_sell_until, available_for_order, created_at, updated_at')
    .eq('menu_id', menuId)
    .order('display_order', { ascending: true });
  if (error) throw error;

  // Fetch category names for items with a category_id
  const catIds = [...new Set((data ?? []).map(i => i.category_id).filter(Boolean))] as string[];
  const catMap = new Map<string, string>();
  if (catIds.length > 0) {
    const { data: cats } = await supabase
      .from('menu_categories')
      .select('id, name')
      .in('id', catIds);
    for (const c of cats ?? []) catMap.set(c.id, c.name);
  }

  return (data ?? []).map(i => ({ ...i, category: catMap.get(i.category_id ?? '') ?? '' })) as MenuItem[];
}

export async function getMenuItemOptionGroups(
  menuId: string,
): Promise<Array<MenuItemOptionGroup & { options?: MenuItemOption[] }>> {
  if (!menuId) return [];

  // ADR-37 slice 2 (P1-2d): the modifier tables (modifier_groups,
  // modifier_group_options, menu_item_modifier_groups, etc.) have deny-all RLS,
  // so the anon client cannot read them directly. Fetch from the public API
  // route, which uses the service role and merges per-item + reusable groups.
  try {
    const { resolveApiRequestUrl } = await import('@/lib/routing/api-requests');
    const res = await fetch(resolveApiRequestUrl(`/api/public/menus/${menuId}/option-groups`), {
      cache: 'no-store',
    });
    if (!res.ok) return [];
    const body = await res.json() as { ok?: boolean; groups?: Array<MenuItemOptionGroup & { options?: MenuItemOption[] }> };
    return body?.groups ?? [];
  } catch {
    return [];
  }
}

export async function getMenuItemAvailabilityData(menuId: string): Promise<{
  rulesByItemId: Map<string, MenuItemAvailabilityRule[]>;
  exceptionsByItemId: Map<string, MenuItemAvailabilityException[]>;
}> {
  if (!menuId) {
    return {
      rulesByItemId: new Map(),
      exceptionsByItemId: new Map(),
    };
  }

  const { data: menuItems, error: menuItemsError } = await supabase
    .from('menu_items')
    .select('id')
    .eq('menu_id', menuId);

  if (menuItemsError) throw menuItemsError;

  const menuItemIds = (menuItems || []).map((item) => item.id).filter(Boolean);
  if (menuItemIds.length === 0) {
    return {
      rulesByItemId: new Map(),
      exceptionsByItemId: new Map(),
    };
  }

  const [{ data: rules, error: rulesError }, { data: exceptions, error: exceptionsError }] = await Promise.all([
    supabase
      .from('menu_item_availability_rules')
      .select('id, menu_item_id, timezone, day_of_week, start_time, end_time, is_active, created_at, updated_at')
      .in('menu_item_id', menuItemIds)
      .order('day_of_week', { ascending: true })
      .order('start_time', { ascending: true }),
    supabase
      .from('menu_item_availability_exceptions')
      .select('id, menu_item_id, exception_date, start_time, end_time, is_available, reason, created_at, updated_at')
      .in('menu_item_id', menuItemIds)
      .order('exception_date', { ascending: true }),
  ]);

  if (rulesError) throw rulesError;
  if (exceptionsError) throw exceptionsError;

  const rulesByItemId = new Map<string, MenuItemAvailabilityRule[]>();
  for (const rule of (rules || []) as MenuItemAvailabilityRule[]) {
    const existing = rulesByItemId.get(rule.menu_item_id) || [];
    existing.push(rule);
    rulesByItemId.set(rule.menu_item_id, existing);
  }

  const exceptionsByItemId = new Map<string, MenuItemAvailabilityException[]>();
  for (const exception of (exceptions || []) as MenuItemAvailabilityException[]) {
    const existing = exceptionsByItemId.get(exception.menu_item_id) || [];
    existing.push(exception);
    exceptionsByItemId.set(exception.menu_item_id, existing);
  }

  return { rulesByItemId, exceptionsByItemId };
}

export async function getBranchMenuItemServiceOverrides(args: {
  branchId?: string | null;
  menuId: string;
}): Promise<Map<string, BranchMenuItemServiceOverride>> {
  if (!args.branchId || !args.menuId) {
    return new Map();
  }

  const { data: menuItems, error: menuItemsError } = await supabase
    .from('menu_items')
    .select('id')
    .eq('menu_id', args.menuId);

  if (menuItemsError) throw menuItemsError;

  const menuItemIds = (menuItems || []).map((item) => item.id).filter(Boolean);
  if (menuItemIds.length === 0) {
    return new Map();
  }

  const { data: overrides, error: overridesError } = await supabase
    .from('branch_menu_item_service_overrides')
    .select('*')
    .eq('branch_id', args.branchId)
    .in('menu_item_id', menuItemIds);

  if (overridesError) throw overridesError;

  return new Map(
    ((overrides || []) as BranchMenuItemServiceOverride[]).map((entry) => [entry.menu_item_id, entry]),
  );
}

// ✅ Save option groups for a menu item (upserts groups + options)
export async function saveOptionGroups(
  menuItemId: string,
  groups: Array<{
    id?: string;
    name: string;
    min_select?: number;
    max_select?: number;
    required?: boolean;
    display_order?: number;
    options?: Array<{
      id?: string;
      name: string;
      price_delta?: number;
      is_default?: boolean;
      display_order?: number;
      recipe_menu_item_id?: string | null;
    }>;
  }>,
) {
  await requireAuth();
  // Every group that survives this save, including the ones inserted below.
  // Built from the payload ids alone, a brand-new group was inserted and then
  // swept as "deleted" in the same call — the same bug the option sweep had.
  const keptGroupIds = new Set<string>();
  for (let gi = 0; gi < groups.length; gi++) {
    const group = groups[gi];
    let groupId = group.id || '';

    if (groupId) {
      const { error: updateError } = await supabase
        .from('menu_item_option_groups')
        .update({
          name: group.name,
          min_select: group.min_select ?? 0,
          max_select: group.max_select ?? 1,
          required: group.required ?? false,
          display_order: group.display_order ?? gi,
        })
        .eq('id', groupId);
      if (updateError) throw updateError;
    } else {
      const { data: newGroup, error: insertError } = await supabase
        .from('menu_item_option_groups')
        .insert({
          menu_item_id: menuItemId,
          name: group.name,
          min_select: group.min_select ?? 0,
          max_select: group.max_select ?? 1,
          required: group.required ?? false,
          display_order: group.display_order ?? gi,
        })
        .select('id')
        .single();
      if (insertError) throw insertError;
      groupId = newGroup.id;
    }
    keptGroupIds.add(groupId);

    // Upsert options for this group.
    //
    // `keptOptionIds` must collect the ids of EVERY row that survives this
    // save , both the ones the editor sent back with an id and the ones we
    // insert here. It used to hold only the pre-existing ids, and the sweep
    // below reads the table AFTER the inserts, so a brand-new option was
    // written and then deleted in the same call: adding a choice to a group
    // that already had one silently did nothing.
    const keptOptionIds = new Set<string>();
    for (let oi = 0; oi < (group.options || []).length; oi++) {
      const opt = (group.options || [])[oi];
      if (opt.id) {
        keptOptionIds.add(opt.id);
        const { error: updateOptError } = await supabase
          .from('menu_item_options')
          .update({
            name: opt.name,
            price_delta: opt.price_delta ?? 0,
            is_default: opt.is_default ?? false,
            display_order: opt.display_order ?? oi,
            recipe_menu_item_id: opt.recipe_menu_item_id ?? null,
            image: (opt as any).image ?? null,
          })
          .eq('id', opt.id);
        if (updateOptError) throw updateOptError;
      } else {
        const { data: newOption, error: insertOptError } = await supabase
          .from('menu_item_options')
          .insert({
            option_group_id: groupId,
            name: opt.name,
            price_delta: opt.price_delta ?? 0,
            is_default: opt.is_default ?? false,
            display_order: opt.display_order ?? oi,
            recipe_menu_item_id: opt.recipe_menu_item_id ?? null,
            image: (opt as any).image ?? null,
          })
          .select('id')
          .single();
        if (insertOptError) throw insertOptError;
        if (newOption?.id) keptOptionIds.add(newOption.id);
      }
    }

    // Remove options the owner deleted from the group. This runs
    // unconditionally: guarding it on a non-empty kept set meant that clearing
    // every choice out of a group left the old rows in the table, and guests
    // kept being offered choices the merchant had removed.
    const { data: currentOptions, error: currentOptsError } = await supabase
      .from('menu_item_options')
      .select('id')
      .eq('option_group_id', groupId);
    if (currentOptsError) throw currentOptsError;
    const toDeleteOptions = (currentOptions || [])
      .map((o) => o.id)
      .filter((id) => !keptOptionIds.has(id));
    if (toDeleteOptions.length > 0) {
      const { error: deleteOptError } = await supabase
        .from('menu_item_options')
        .delete()
        .in('id', toDeleteOptions);
      if (deleteOptError) throw deleteOptError;
    }
  }

  // Remove groups that were deleted
  const { data: currentGroups, error: currentGroupsError } = await supabase
    .from('menu_item_option_groups')
    .select('id')
    .eq('menu_item_id', menuItemId);
  if (currentGroupsError) throw currentGroupsError;
  const toDelete = (currentGroups || [])
    .map((g) => g.id)
    .filter((id) => !keptGroupIds.has(id));
  if (toDelete.length > 0) {
    const { error: deleteGroupError } = await supabase
      .from('menu_item_option_groups')
      .delete()
      .in('id', toDelete);
    if (deleteGroupError) throw deleteGroupError;
  }

  return { ok: true };
}

// ✅ Get a template by ID (searches both user and built-in tables)
export async function getTemplate(id: string): Promise<any | null> {
  // Try user templates first
  const { data: userData, error: userError } = await supabase
    .from('templates')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (userError) throw userError;
  if (userData) return userData;

  // Fallback to built-in templates
  const { data: builtinData, error: builtinError } = await supabase
    .from('menu_templates')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (builtinError) throw builtinError;
  if (builtinData) return builtinData;

  return null;
}

// ✅ Get all templates (combines user and built-in)
export async function getTemplates(options?: { limit?: number; offset?: number }): Promise<any[]> {
  const limit = options?.limit;
  const offset = options?.offset;

  // Fetch user templates
  let userQuery = supabase
    .from('templates')
    .select('*');
  if (limit) {
    userQuery = userQuery.limit(limit);
  }
  if (offset) {
    userQuery = userQuery.range(offset, offset + (limit ?? 1000) - 1);
  }
  const { data: userTemplates, error: userError } = await userQuery;
  if (userError) throw userError;

  // Fetch built-in templates
  let builtinQuery = supabase
    .from('menu_templates')
    .select('*');
  if (limit) {
    builtinQuery = builtinQuery.limit(limit);
  }
  if (offset) {
    builtinQuery = builtinQuery.range(offset, offset + (limit ?? 1000) - 1);
  }
  const { data: builtinTemplates, error: builtinError } = await builtinQuery;
  if (builtinError) throw builtinError;

  return [...(userTemplates ?? []), ...(builtinTemplates ?? [])];
}
